import { useCallback, useEffect, useMemo, useRef } from "react";
import {
  buildChatDraftStorageKey,
  loadChatDraft,
  removeChatDraft,
  saveChatDraft,
} from "../utils/chatDrafts";

export default function useChatDraftPersistence({
  userId,
  chatIdentity,
  value,
  setValue,
  inputRef,
  disabled = false,
}) {
  const storageKey = useMemo(
    () => buildChatDraftStorageKey(userId, chatIdentity),
    [chatIdentity, userId]
  );
  const latestValueRef = useRef(String(value ?? ""));
  const memoryDraftRef = useRef("");
  const disabledRef = useRef(Boolean(disabled));
  const hydratedStorageKeyRef = useRef(null);

  latestValueRef.current = String(value ?? "");
  disabledRef.current = Boolean(disabled);

  const persistDraft = useCallback(
    (nextValue) => {
      if (disabledRef.current) return;
      const text = String(nextValue ?? "");
      memoryDraftRef.current = text;
      saveChatDraft(storageKey, text);
    },
    [storageKey]
  );

  const clearDraft = useCallback(() => {
    latestValueRef.current = "";
    memoryDraftRef.current = "";
    setValue("");
    if (inputRef?.current) {
      inputRef.current.value = "";
    }
    removeChatDraft(storageKey);
  }, [inputRef, setValue, storageKey]);

  const restoreDraft = useCallback(
    (nextValue) => {
      const text = String(nextValue ?? "");
      latestValueRef.current = text;
      memoryDraftRef.current = text;
      setValue(text);
      if (inputRef?.current) {
        inputRef.current.value = text;
      }
      saveChatDraft(storageKey, text);
    },
    [inputRef, setValue, storageKey]
  );

  useEffect(() => {
    if (!storageKey || disabled) return;
    if (hydratedStorageKeyRef.current === storageKey) return;
    hydratedStorageKeyRef.current = storageKey;

    const currentValue = String(inputRef?.current?.value ?? latestValueRef.current ?? "");
    if (currentValue) {
      memoryDraftRef.current = currentValue;
      saveChatDraft(storageKey, currentValue);
      return;
    }

    const savedDraft = memoryDraftRef.current || loadChatDraft(storageKey);
    if (!savedDraft) return;

    memoryDraftRef.current = savedDraft;
    latestValueRef.current = savedDraft;
    setValue(savedDraft);
  }, [disabled, inputRef, setValue, storageKey]);

  useEffect(() => {
    if (typeof window === "undefined" || typeof document === "undefined") return undefined;

    const persistCurrentDraft = () => {
      if (disabledRef.current) return;
      persistDraft(inputRef?.current?.value ?? latestValueRef.current);
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") persistCurrentDraft();
    };

    window.addEventListener("pagehide", persistCurrentDraft);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      window.removeEventListener("pagehide", persistCurrentDraft);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [inputRef, persistDraft]);

  return { clearDraft, persistDraft, restoreDraft, storageKey };
}
